import { useAuth } from "hooks/auth";
import { InfiniteResponse } from "hooks/useManageInfiniteScroll";
import { querystring } from "models/Listing";
import useSWR, { SWRResponse } from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRInfinite from "swr/infinite";
import { ListingProps, ListResponse } from "types/listing";
import { RoleBackendModel, RoleFields } from "types/role";
import { getEnvVariable } from "utilities/env";

export const useRoles = () => {
  const { fetcher } = useAuth();

  const useRoleList = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<RoleBackendModel>> => {
    return useSWR(
      `${getEnvVariable("REACT_APP_API_URL")}/acl/role${querystring(props)}`,
      fetcher,
    );
  };
  const useInfiniteRolesList = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<RoleBackendModel>>(
      (index) =>
        `${getEnvVariable(
          "REACT_APP_API_URL",
        )}/acl/role${querystring({ ...props, page: index + 1 })}`,
      fetcher,
      { revalidateFirstPage: false },
    );
  };
  const deleteRole = async (roleID: string) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/acl/role/${roleID}`,
      { body: JSON.stringify({ role: roleID }), method: "delete" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };
  const addRole = async (payload: RoleFields) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/acl/role`,
      {
        body: JSON.stringify({ role: payload }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response;
    if (response.status === 204) {
      return { data };
    } else {
      throw await response.json();
    }
  };
  const editRole = async (roleID: string, payload: RoleFields) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/acl/role/${roleID}`,
      {
        body: JSON.stringify({ role: payload }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    const data = await response;
    if (response.status === 204) {
      return { data };
    } else {
      throw await response.json();
    }
  };
  const useRoleData = (roleID: string) =>
    useSWR(
      () =>
        !!roleID
          ? `${getEnvVariable("REACT_APP_API_URL")}/acl/role/${roleID}`
          : null,
      fetcher,
    );

  const useResources = () => {
    return useSWRImmutable(
      () => `${getEnvVariable("REACT_APP_API_URL")}/acl/resources`,
      fetcher,
    );
  };
  const useAccesses = () => {
    return useSWR(
      `${getEnvVariable("REACT_APP_API_URL")}/acl/accesses`,
      fetcher,
    );
  };
  return {
    addRole,
    deleteRole,
    editRole,
    useAccesses,
    useResources,
    useRoleData,
    useRoleList,
    useInfiniteRolesList,
  };
};
